import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Libraries } from '@react-google-maps/api/dist/utils/make-load-script-url';

import { StyledFormControl } from '../styled/Inputs';
import { AddressFieldType, AddressLabels, AddressObject, IUAddressLabel } from './AddressFields';
import { DeleteIcon, DropDownClickable, FieldCol } from '../styled/ContactsForm';
import trash from '../../core/assets/trash.png';
import GeocoderAddressComponent = google.maps.GeocoderAddressComponent;
import GAutocomplete = google.maps.places.Autocomplete;

declare const AUTOCOMPLETE_KEY: string;

interface Props {
  handleDelete: () => void;
  handleChange: (value: string, fieldAttribute: AddressFieldType) => void;
  handleLabelChange: (address: AddressObject, previousLabel: IUAddressLabel) => void;
  handleFullChange: (address: AddressObject) => void;
  address: AddressObject;
  hideLabel?: boolean;
  autocomplete?: boolean;
}

const LIBRARIES: Libraries = ['places'];

const AddressField = ({
  address,
  handleDelete,
  handleLabelChange,
  handleChange,
  handleFullChange,
  hideLabel = false,
  autocomplete = false
}: Props) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: AUTOCOMPLETE_KEY,
    libraries: LIBRARIES,
  });

  const add1Ref = useRef<HTMLInputElement>(null);
  const [gautocomplete, setGautocomplete] = useState<GAutocomplete | null>(null);
  const [address1, setAddress1] = useState<string>('');
  const [address2, setAddress2] = useState<string>('');
  const [label, setLabel] = useState<IUAddressLabel>(address.label ?? IUAddressLabel.OtherAddress);

  useEffect(() => {
    setLabel(address.label);
  }, [address.label]);

  const onLoad = (autocomplete: GAutocomplete) => {
    autocomplete.setFields(['address_components', 'name']);
    setGautocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (gautocomplete === null) {
      return;
    }

    const addressObject = gautocomplete.getPlace();
    const addressComponents = addressObject.address_components;
    const address1Component = add1Ref.current;

    if (addressComponents && address1Component) {
      const name = (!addressObject?.name?.match(/^\d/) && addressObject.name + ', ') || '';
      const city = addressComponents.filter((component: GeocoderAddressComponent) =>
        component.types.includes('locality')
      )[0];
      const country = addressComponents.filter((component: GeocoderAddressComponent) =>
        component.types.includes('country')
      )[0];
      const admin1 = addressComponents.filter((component: GeocoderAddressComponent) =>
        component.types.includes('administrative_area_level_1')
      )[0];
      const zip = addressComponents.filter((component: GeocoderAddressComponent) =>
        component.types.includes('postal_code')
      )[0];

      const address1Value = address.address.address1 = `${name}${addressComponents[0].long_name} ${addressComponents[1].long_name}`;
      const cityValue = address.address.city = city?.long_name ?? '';
      address.address.country = country?.long_name ?? '';
      const admin1Value = address.address.admin1 = admin1?.short_name ?? '';
      const zipValue = address.address.postalCode = zip?.short_name ?? '';

      address1Component.value = `${address1Value} ${cityValue} ${admin1Value} ${zipValue}`;
      setAddress1(address1Component.value);
    }

    handleFullChange(address);
  };

  const handleAddress1Change = (e: ChangeEvent<HTMLInputElement>) => {
    const address1 = e.target.value;

    setAddress1(address1);
    handleChange(address1, 'address1');
  };

  const handleAddress2Change = (e: ChangeEvent<HTMLInputElement>) => {
    const address2 = e.target.value;

    setAddress2(address2);
    handleChange(address2, 'address2');
  };

  if (!isLoaded) {
    return null;
  }

  const onLabelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newLabel = +e.target.value as IUAddressLabel;

    setLabel(newLabel);

    handleLabelChange({
      address: address.address,
      label: newLabel
    }, label);
  };

  const options = Object.keys(IUAddressLabel)
    .filter((key) => {
      const labelOption = Number(key) as IUAddressLabel;

      if (isNaN(labelOption)) {
        return false;
      }

      return true;
    })
    .map((labelStr) => {
      const label = +labelStr as IUAddressLabel;
      return <option value={label} key={label}>{AddressLabels[label]}</option>;
    });

  return (
    <Autocomplete
      onLoad={onLoad}
      onPlaceChanged={onPlaceChanged}
    >
      <div>
        <Form.Group as={Row}>
          <FieldCol>
            <StyledFormControl
              autoComplete={autocomplete && (label === IUAddressLabel.PersonalAddress || hideLabel) ? 'street-address' : 'do-not-autocomplete'}
              name="street-address"
              className="google-auto-complete-input"
              value={address1}
              ref={add1Ref}
              onChange={handleAddress1Change}
              placeholder="Address"
            />
          </FieldCol>
        </Form.Group>
        <Form.Group as={Row} className="gx-2">
          <FieldCol xs={hideLabel ? 12 : 6}>
            <StyledFormControl
              autoComplete={autocomplete && (label === IUAddressLabel.PersonalAddress || hideLabel) ? 'address-line2' : 'do-not-autocomplete'}
              name="address-line2"
              value={address2}
              onChange={handleAddress2Change}
              placeholder="Unit"
            />
          </FieldCol>
          {
            hideLabel ? null :
              <>
                <FieldCol xs={5}>
                  <DropDownClickable
                    value={label}
                    onChange={onLabelChange}
                  >
                    {options}
                  </DropDownClickable>
                </FieldCol>
                <FieldCol xs={1}>
                  <DeleteIcon src={trash} onClick={handleDelete}/>
                </FieldCol>
              </>
          }
        </Form.Group>
      </div>
    </Autocomplete>
  );
};

export default AddressField;
